import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';
import offer, {
  CALL_PORTRAIT,
  HERO_IMAGE,
  callMinutes,
  discountPercent,
  euroAmount,
  formatCount,
  isFilled,
  percentLabel,
  placesFillPercent,
  reviewLine,
  shownOriginalPrice,
} from '../config/offer';

/**
 * OpenBro V5. Dark earth-from-orbit hero, served at /v5 and on the homepage.
 * Prices, proof, capacity, and call length come from src/config/offer.js.
 * Empty facts are omitted. French and English share the same rules.
 */

const PROGRAMS = {
  fr: [
    ['/v3-business.jpg', 'BUSINESS'],
    ['/v3-career.jpg', 'IA'],
    ['/v3-focus.jpg', 'VIDÉO & RÉSEAUX'],
    ['/cover-train.jpg', 'SPORT & NUTRITION'],
    ['/v3-money.jpg', 'E-COMMERCE'],
    ['/v3-relations.jpg', 'RELATIONS'],
  ],
  en: [
    ['/v3-business.jpg', 'BUSINESS'],
    ['/v3-career.jpg', 'AI'],
    ['/v3-focus.jpg', 'VIDEO & SOCIAL'],
    ['/cover-train.jpg', 'SPORT & NUTRITION'],
    ['/v3-money.jpg', 'E-COMMERCE'],
    ['/v3-relations.jpg', 'RELATIONSHIPS'],
  ],
};

const NAV = [
  { id: 'discipline', fr: 'Discipline', en: 'Discipline', href: null },
  { id: 'formation', fr: 'Formation', en: 'Training', href: '#formations' },
  { id: 'mentorat', fr: 'Mentorat', en: 'Mentorship', href: '#mentorat' },
  { id: 'communaute', fr: 'Communauté', en: 'Community', href: null },
  { id: 'resultats', fr: 'Résultats', en: 'Results', href: null },
];

const COPY = {
  fr: {
    lang: 'FR',
    title: 'PRENDS\nLE CONTRÔLE\nDE TA VIE.',
    lead: 'Un écosystème complet pour avancer plus vite et gagner plus.',
    pointTitles: [
      'Un appel privé chaque mois',
      'Une IA privée dispo 24/7',
      'Un plan d’action personnalisé',
      'Une communauté privée',
    ],
    membersWord: 'membres',
    membersSub: 'Entrepreneurs, créateurs, investisseurs...',
    followersLabel: 'abonnés sur TikTok et Instagram',
    cta: 'Commencer maintenant →',
    launch: 'Offre de lancement',
    only: 'SEULEMENT',
    period: '/mois',
    cancel: 'Résiliable\nà tout moment',
    kickerLead: 'Mentorat privé · ',
    kickerTail: 'en tête-à-tête',
    callTitle: 'Des appels privés\navec moi.',
    progTitle: 'Des programmes\nde formation exclusifs.',
    progLink: 'Voir tous les programmes',
    progBody: 'Des modules concrets et actionnables pour progresser dans tous les domaines de ta vie.',
    trust: [
      ['lock', 'Paiement sécurisé'],
      ['cal', 'Résiliable à tout moment'],
      ['bolt', 'Accès immédiat'],
    ],
    perksTail: [
      'IA privée disponible 24/7',
      'Tous les programmes de formation',
      'Plan d’action personnalisé',
      'Suivi et bilans mensuels',
      'Accès à la communauté privée',
      'Accessible sur mobile et ordinateur',
      'Mise à jour tous les jours à 6h',
    ],
  },
  en: {
    lang: 'EN',
    title: 'TAKE\nCONTROL\nOF YOUR LIFE.',
    lead: 'A complete system to move faster and earn more.',
    pointTitles: [
      'A private call every month',
      'A private AI, available 24/7',
      'A personal action plan',
      'A private community',
    ],
    membersWord: 'members',
    membersSub: 'Founders, creators, investors...',
    followersLabel: 'followers on TikTok and Instagram',
    cta: 'Start now →',
    launch: 'Launch offer',
    only: 'ONLY',
    period: '/month',
    cancel: 'Cancel\nanytime',
    kickerLead: 'Private mentorship · ',
    kickerTail: 'face to face',
    callTitle: 'Private calls\nwith me.',
    progTitle: 'Exclusive\ntraining programs.',
    progLink: 'See all programs',
    progBody: 'Concrete modules you can use, across every part of your life.',
    trust: [
      ['lock', 'Secure payment'],
      ['cal', 'Cancel anytime'],
      ['bolt', 'Instant access'],
    ],
    perksTail: [
      'Private AI available 24/7',
      'Every training program',
      'A personal action plan',
      'Monthly check-ins',
      'Private community access',
      'Available on phone and computer',
      'Updated every day at 6h',
    ],
  },
};

function pointDetail(lang, minutes) {
  if (lang === 'en') {
    return minutes
      ? `${minutes} min face to face to clear what’s blocking you and adjust your strategy.`
      : 'Face to face, to clear what’s blocking you and adjust your strategy.';
  }
  return minutes
    ? `${minutes}\u00A0min en tête-à-tête pour lever tes blocages et ajuster ta stratégie.`
    : 'En tête-à-tête pour lever tes blocages et ajuster ta stratégie.';
}

function callBody(lang, minutes) {
  if (lang === 'en') {
    return minutes
      ? `Each month we sit down face to face for ${minutes} min to clear what’s blocking you, adjust your strategy, and make sure you keep moving.`
      : 'Each month we sit down face to face to clear what’s blocking you, adjust your strategy, and make sure you keep moving.';
  }
  return minutes
    ? `Chaque mois, on fait le point en tête-à-tête pendant ${minutes}\u00A0min pour lever tes blocages, ajuster ta stratégie et t’assurer d’avancer.`
    : 'Chaque mois, on fait le point en tête-à-tête pour lever tes blocages, ajuster ta stratégie et t’assurer d’avancer.';
}

function includedLine(lang, minutes) {
  if (lang === 'en') {
    return minutes
      ? `${minutes} min · once a month · included in the subscription`
      : 'Once a month · included in the subscription';
  }
  return minutes
    ? `${minutes}\u00A0min · une fois par mois · inclus dans l’abonnement`
    : 'Une fois par mois · inclus dans l’abonnement';
}

function callPerk(lang, minutes) {
  if (lang === 'en') {
    return minutes ? `A private ${minutes}-min call each month` : 'A private call each month';
  }
  return minutes ? `Un appel privé de ${minutes}\u00A0min par mois` : 'Un appel privé par mois';
}

function capacityLine(lang, count) {
  const formatted = formatCount(count, lang);
  if (lang === 'en') {
    return `I only take ${formatted} people a month: I run every call myself.`;
  }
  return `Je ne prends que ${formatted} personnes par mois : je fais chaque appel moi-même.`;
}

function spotsLine(lang, count) {
  const formatted = formatCount(count, lang);
  if (lang === 'en') return `${formatted} spots left this month`;
  return `Plus que ${formatted} places ce mois-ci`;
}

function otherPointDetails(lang) {
  if (lang === 'en') {
    return [
      'To keep moving between calls, at any hour.',
      'Built around your situation and your goals.',
      'Ambitious people who actually take action.',
    ];
  }
  return [
    'Pour avancer entre deux appels, à n’importe quelle heure.',
    'Adapté à ta situation et à tes objectifs.',
    'Des entrepreneurs ambitieux qui passent à l’action.',
  ];
}

function OptimizedImage({ src, alt = '', lazy = true, priority = false, className }) {
  const base = src.replace(/\.(jpe?g|png)$/i, '');
  return (
    <picture className={className}>
      <source srcSet={`${base}.avif`} type="image/avif" />
      <source srcSet={`${base}.webp`} type="image/webp" />
      <img
        src={src}
        alt={alt}
        loading={lazy ? 'lazy' : 'eager'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
      />
    </picture>
  );
}

function PointIcon({ index }) {
  const icons = [
    <path key="call" d="M7 4h3l1 4-2 1a12 12 0 0 0 6 6l1-2 4 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 6a2 2 0 0 1 2-2z" />,
    <path key="ai" d="M12 3.2 13.4 7 17.2 8.4 13.4 9.8 12 13.6 10.6 9.8 6.8 8.4 10.6 7 12 3.2zM18 13.2l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8zM6.2 14l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6.6-1.6z" />,
    <path key="plan" d="M8 6h11M8 12h11M8 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />,
    <path key="people" d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 21v-2a3.8 3.8 0 0 0-2.8-3.6M16.2 4.2a3.5 3.5 0 0 1 0 6.6" />,
  ];
  return (
    <span className="v3-ico">
      <svg viewBox="0 0 24 24" aria-hidden="true">{icons[index]}</svg>
    </span>
  );
}

function TrustIcon({ name }) {
  const paths = {
    lock: 'M8 11V8a4 4 0 0 1 8 0v3M7 11h10v8H7z',
    cal: 'M7 6h10v12H7zM7 10h10M10 4v4M14 4v4',
    bolt: 'M13 3 6 13h5l-1 8 8-12h-5l0-6z',
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

function PayButton({ label, onClick, disabled, buttonRef, className }) {
  return (
    <span className={className ? `v3-ring ${className}` : 'v3-ring'}>
      <button type="button" ref={buttonRef} disabled={disabled} onClick={onClick}>
        {disabled ? '...' : label}
      </button>
    </span>
  );
}

function SocialProof({ lang, copy }) {
  const avatars = Array.isArray(offer.memberAvatars) ? offer.memberAvatars.filter(Boolean) : [];
  const reviews = reviewLine(lang);
  const showMembers = isFilled(offer.payingMembers);
  const showFollowers = !showMembers && isFilled(offer.socialFollowers);
  if (!showMembers && !showFollowers && !reviews && avatars.length === 0) return null;

  const count = showMembers ? offer.payingMembers : offer.socialFollowers;
  const headline = (showMembers || showFollowers)
    ? `${formatCount(count, lang)} ${showMembers ? copy.membersWord : copy.followersLabel}`
    : null;

  return (
    <div className="v3-social">
      {avatars.length > 0 && (
        <div className="v3-faces">
          {avatars.map((src) => (
            <img key={src} src={src} alt="" loading="lazy" decoding="async" />
          ))}
        </div>
      )}
      <div>
        {reviews && <p className="v3-stars">{reviews}</p>}
        {headline && <strong>{headline}</strong>}
        {showMembers && <span>{copy.membersSub}</span>}
      </div>
    </div>
  );
}

export default function OpenBroV5() {
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const copy = COPY[lang] || COPY.fr;
  const minutes = callMinutes();
  const previousPrice = shownOriginalPrice();
  const discount = discountPercent();
  const fill = placesFillPercent();
  const heroCtaRef = useRef(null);
  const boardRef = useRef(null);
  const [pastHeroCta, setPastHeroCta] = useState(false);
  const details = [pointDetail(lang, minutes), ...otherPointDetails(lang)];
  const perks = [callPerk(lang, minutes), ...copy.perksTail];

  const pay = () => {
    if (isActive) return;
    if (isFilled(offer.paymentUrl)) {
      window.location.assign(offer.paymentUrl);
      return;
    }
    startCheckout('v5-cta');
  };

  useEffect(() => {
    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, []);

  useEffect(() => {
    const frame = document.querySelector('.v3');
    const board = boardRef.current;
    if (!frame || !board) return undefined;
    const fit = () => {
      const frameRect = frame.getBoundingClientRect();
      let scale = 1;
      board.style.transformOrigin = 'top left';
      for (let pass = 0; pass < 6; pass += 1) {
        board.style.transform = 'none';
        board.style.width = scale < 0.995 ? `${100 / scale}%` : '100%';
        const boardRect = board.getBoundingClientRect();
        const last = board.lastElementChild;
        const needed = (last ? last.getBoundingClientRect().bottom : boardRect.bottom) - boardRect.top;
        const available = frameRect.bottom - boardRect.top;
        const next = Math.min(1, available / Math.max(needed, 1));
        if (Math.abs(next - scale) < 0.01) {
          scale = next;
          break;
        }
        scale = next;
      }
      if (scale < 0.995) {
        board.style.width = `${100 / scale}%`;
        board.style.transform = `scale(${scale})`;
        return;
      }
      board.style.width = '100%';
      board.style.transform = 'none';
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(frame);
    window.addEventListener('resize', fit);
    document.fonts?.ready.then(fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [lang]);

  useEffect(() => {
    const node = heroCtaRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      setPastHeroCta(passed);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="v3">
      <OptimizedImage src={HERO_IMAGE} alt="" lazy={false} priority className="v3-bg" />
      <div className="v3-scrim" aria-hidden="true" />
      {offer.portraitSrc ? <img className="v3-hero-portrait" src={offer.portraitSrc} alt="" /> : null}

      <div className="v3-board" ref={boardRef}>
      <header className="v3-top">
        <div className="v3-logo">OpenBro<span>✦</span></div>
        <nav className="v3-nav" aria-label="OpenBro">
          {NAV.map((item) => {
            const label = lang === 'en' ? item.en : item.fr;
            return item.href
              ? <a key={item.id} href={item.href}>{label}</a>
              : <span key={item.id}>{label}</span>;
          })}
        </nav>
        <div className="v3-top-actions">
          <button type="button" className="v3-lang" onClick={toggleLang} aria-label={lang === 'en' ? 'Switch language' : 'Changer de langue'}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17M12 3.5c2.2 2.4 2.2 14.6 0 17M12 3.5c-2.2 2.4-2.2 14.6 0 17" /></svg>
            {copy.lang}
          </button>
          <PayButton label={copy.cta} onClick={pay} disabled={isActive} />
        </div>
      </header>

      <main className="v3-main">
        <div className="v3-copy">
          <h1 className="v3-title">{copy.title}</h1>
          <p className="v3-lead">{copy.lead}</p>
        </div>

        <div className="v3-cta-block">
          <SocialProof lang={lang} copy={copy} />
          <PayButton className="v3-ring-wide" label={copy.cta} onClick={pay} disabled={isActive} buttonRef={heroCtaRef} />
          <ul className="v3-trust">
            {copy.trust.map(([icon, label]) => (
              <li key={label}><TrustIcon name={icon} />{label}</li>
            ))}
          </ul>
        </div>

        <article className="v3-card v3-call" id="mentorat">
          <div>
            <p className="v3-kicker">{copy.kickerLead}<span className="v3-keep">{copy.kickerTail}</span></p>
            <h2>{copy.callTitle}</h2>
            <p>{callBody(lang, minutes)}</p>
            <p className="v3-included">{includedLine(lang, minutes)}</p>
          </div>
          <div className="v3-portrait">
            <OptimizedImage src={CALL_PORTRAIT} alt="" />
          </div>
        </article>

        <ul className="v3-points">
          {copy.pointTitles.map((title, index) => (
            <li key={title}>
              <PointIcon index={index} />
              <div>
                <strong>{title}</strong>
                <span>{details[index]}</span>
              </div>
            </li>
          ))}
        </ul>

        <article className="v3-card v3-price">
          <div className="v3-offer">
            <span className="v3-launch">{copy.launch}</span>
            {isFilled(offer.monthlyCapacity) && <p className="v3-capacity">{capacityLine(lang, offer.monthlyCapacity)}</p>}
            {isFilled(offer.placesRemaining) && <p className="v3-spots">{spotsLine(lang, offer.placesRemaining)}</p>}
            {fill !== null && (
              <span className="v3-meter" aria-hidden="true"><i style={{ width: `${fill}%` }} /></span>
            )}
          </div>
          <div className="v3-price-top">
            <div>
              <p className="v3-only">{copy.only}</p>
              <div className="v3-price-line">
                <strong>{euroAmount(offer.price)}</strong>
                <em>{copy.period}</em>
                {previousPrice !== null && <s>{euroAmount(previousPrice)}</s>}
                {discount !== null && <b className="v3-off">-{percentLabel(discount)}</b>}
              </div>
            </div>
            <span className="v3-cancel">
              <i aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M13 3 6 13h5l-1 8 8-12h-5l0-6z" /></svg>
              </i>
              {copy.cancel}
            </span>
          </div>
          <ul className="v3-perks">
            {perks.map((perk) => <li key={perk}>{perk}</li>)}
          </ul>
        </article>

        <article className="v3-card v3-prog" id="formations">
          <div className="v3-prog-head">
            <h2>{copy.progTitle}</h2>
            <a className="v3-more" href="#formations">{copy.progLink}</a>
          </div>
          <p>{copy.progBody}</p>
          <ul className="v3-thumbs">
            {(PROGRAMS[lang] || PROGRAMS.fr).map(([src, name]) => (
              <li key={name}>
                <OptimizedImage src={src} alt="" />
                <b>{name}</b>
              </li>
            ))}
          </ul>
        </article>
      </main>
      </div>

      <div className={pastHeroCta ? 'v3-sticky is-on' : 'v3-sticky'}>
        <PayButton className="v3-ring-wide" label={copy.cta} onClick={pay} disabled={isActive} />
      </div>

      <style>{`
        .v3, .v3 * { box-sizing: border-box; }
        .v3 { position: fixed; inset: 0; height: 100svh; width: 100%; overflow: hidden; display: flex; flex-direction: column; background: #05060A; color: #fff; font-family: Inter, Helvetica Neue, Arial, sans-serif; padding: 10px 14px 12px; }
        .v3-bg { position: fixed; inset: 0; width: 100%; height: 100svh; z-index: 0; pointer-events: none; }
        .v3-bg img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: 62% center; }
        .v3-scrim { position: fixed; inset: 0; width: 100%; height: 100svh; z-index: 1; pointer-events: none; background: linear-gradient(180deg, rgba(5,6,10,0.78) 0%, rgba(5,6,10,0.62) 34%, rgba(5,6,10,0.84) 100%), linear-gradient(90deg, rgba(5,6,10,0.9) 0%, rgba(5,6,10,0.55) 48%, rgba(5,6,10,0.28) 100%); }
        .v3-hero-portrait { position: absolute; z-index: 1; right: 0; bottom: 0; height: min(72svh, 820px); width: auto; max-width: min(46vw, 520px); object-fit: contain; object-position: right bottom; pointer-events: none; -webkit-mask-image: linear-gradient(90deg, transparent, #000 28%), linear-gradient(180deg, transparent, #000 18% 72%, transparent); -webkit-mask-composite: source-in; mask-image: linear-gradient(90deg, transparent, #000 28%), linear-gradient(180deg, transparent, #000 18% 72%, transparent); mask-composite: intersect; }
        .v3-top, .v3-main, .v3-sticky { position: relative; z-index: 2; }
        .v3-top { display: flex; align-items: center; gap: 8px; }
        .v3-logo { flex: none; font-family: "Inter Tight", Inter, sans-serif; font-weight: 800; font-size: 20px; letter-spacing: -0.045em; color: #fff; }
        .v3-logo span { font-size: 0.48em; margin-left: 1px; }
        .v3-nav { display: none; }
        .v3-nav a, .v3-nav span { font-size: 13px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #fff; text-decoration: none; }
        .v3-nav a { min-height: 44px; display: inline-flex; align-items: center; position: relative; }
        .v3-nav span { color: rgba(255,255,255,0.72); }
        .v3-nav a::after { content: ""; position: absolute; left: 0; right: 0; bottom: 10px; height: 1px; background: currentColor; transform: scaleX(0); transform-origin: left center; transition: transform 0.28s ease; }
        .v3-top-actions { display: flex; align-items: center; gap: 6px; margin-left: auto; }
        .v3-lang { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-width: 44px; min-height: 44px; padding: 0; border: 0; background: none; color: #fff; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
        .v3-lang svg { width: 18px; height: 18px; fill: none; stroke: #fff; stroke-width: 1.6; }
        .v3-ring { position: relative; display: inline-flex; padding: 3px; border-radius: 999px; overflow: hidden; isolation: isolate; background: #05060A; vertical-align: middle; }
        .v3-ring::before { content: ""; position: absolute; left: 50%; top: 50%; width: 220%; aspect-ratio: 1; background: conic-gradient(transparent 0 200deg, #fff 250deg, #fff 290deg, transparent 340deg); animation: v3-spin 1.4s linear infinite; z-index: 0; }
        .v3-ring > button { position: relative; z-index: 1; border: 0; background: #fff; color: #111; border-radius: 999px; font-family: inherit; font-weight: 700; cursor: pointer; min-height: 44px; padding: 0 12px; font-size: 13px; white-space: nowrap; }
        .v3-ring > button:disabled { cursor: wait; }
        .v3-ring-wide { width: 100%; max-width: 440px; }
        .v3-ring-wide > button { width: 100%; min-height: 44px; font-size: 15px; }
        .v3-board { flex: 1; min-height: 0; width: 100%; display: flex; flex-direction: column; }
        .v3-main { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
        .v3-copy { display: contents; }
        .v3-title { margin: 0; font-family: "Inter Tight", Inter, sans-serif; font-weight: 900; font-size: clamp(28px, 7.4vw, 36px); line-height: 0.92; letter-spacing: -0.045em; word-spacing: 0.1em; white-space: pre-line; color: #fff; overflow-wrap: normal; word-break: keep-all; hyphens: none; }
        .v3-lead { margin: 4px 0 0; max-width: 42em; font-size: 13px; line-height: 1.25; font-weight: 500; color: rgba(255,255,255,0.72); }
        .v3-points { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
        .v3-points li { display: grid; grid-template-columns: 32px minmax(0, 1fr); column-gap: 8px; align-items: center; }
        .v3-ico { width: 32px; height: 32px; border-radius: 10px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); display: flex; align-items: center; justify-content: center; }
        .v3-ico svg { width: 18px; height: 18px; fill: none; stroke: #fff; stroke-width: 1.75; stroke-linecap: round; stroke-linejoin: round; }
        .v3-points strong { display: block; font-size: clamp(12px, 1.6svh, 15px); font-weight: 700; line-height: 1.1; letter-spacing: -0.015em; color: #fff; }
        .v3-points span { display: block; margin-top: 1px; font-size: clamp(11px, 1.4svh, 13px); line-height: 1.2; color: rgba(255,255,255,0.72); }
        .v3-social { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
        .v3-faces { display: flex; }
        .v3-faces img { width: 42px; height: 42px; margin-left: -10px; border-radius: 50%; border: 2px solid #05060A; object-fit: cover; display: block; }
        .v3-faces img:first-child { margin-left: 0; }
        .v3-stars { margin: 0 0 4px; color: #9CC8FF; font-size: 14px; font-weight: 700; letter-spacing: 0.02em; }
        .v3-social strong { display: block; font-size: 18px; color: #fff; }
        .v3-social span { display: block; margin-top: 2px; max-width: 36ch; font-size: 13px; line-height: 1.35; color: rgba(255,255,255,0.72); }
        .v3-cta-block { min-height: 0; }
        .v3-trust { display: flex; flex-wrap: wrap; gap: 4px 12px; list-style: none; margin: 6px 0 0; padding: 0; font-size: 12px; color: rgba(255,255,255,0.72); }
        .v3-trust li { display: flex; align-items: center; gap: 6px; }
        .v3-trust svg { width: 16px; height: 16px; fill: none; stroke: #fff; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
        .v3-card { background: rgba(8,10,16,0.78); color: #fff; border-radius: 16px; padding: 8px 10px; border: 1px solid rgba(255,255,255,0.12); box-shadow: 0 18px 50px rgba(0,0,0,0.28); backdrop-filter: blur(18px); min-width: 0; }
        .v3-card h2 { margin: 0; font-family: "Inter Tight", Inter, sans-serif; font-weight: 800; font-size: 18px; line-height: 1.02; letter-spacing: -0.03em; white-space: pre-line; color: #fff; }
        .v3-card p { margin: 3px 0 0; font-size: 12px; line-height: 1.25; color: rgba(255,255,255,0.72); }
        .v3-card .v3-kicker { margin: 0 0 8px; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9CC8FF; }
        .v3-keep { white-space: nowrap; }
        .v3-card .v3-included { margin-top: 12px; color: #9CC8FF; font-weight: 600; }
        .v3-card .v3-capacity, .v3-card .v3-spots { margin: 8px 0 0; font-size: 14px; line-height: 1.4; font-weight: 600; color: #fff; }
        .v3-card .v3-only { margin: 0; letter-spacing: 0.16em; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.72); }
        .v3-call { display: grid; grid-template-columns: minmax(0, 1fr) 84px; gap: 8px; align-items: stretch; }
        .v3-portrait { position: relative; height: 100%; min-height: 96px; border-radius: 12px; overflow: hidden; background: #12141a; }
        .v3-prog > p { display: none; }
        .v3-portrait picture, .v3-portrait img { width: 100%; height: 100%; object-fit: cover; object-position: center 18%; display: block; }
        .v3-offer { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }
        .v3-launch { display: inline-flex; align-items: center; min-height: 28px; padding: 4px 8px; border-radius: 6px; background: rgba(156,200,255,0.12); color: #9CC8FF; border: 1px solid rgba(156,200,255,0.35); text-transform: uppercase; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; }
        .v3-meter { display: block; width: 100%; height: 5px; margin-top: 8px; border-radius: 999px; background: rgba(255,255,255,0.12); overflow: hidden; }
        .v3-meter i { display: block; height: 100%; border-radius: inherit; background: #9CC8FF; transition: width 700ms ease; }
        .v3-price-top { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; }
        .v3-price-line { display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; }
        .v3-price { display: flex; flex-direction: column; }
        .v3-price-line strong { font-family: "Inter Tight", Inter, sans-serif; font-weight: 900; font-size: 36px; letter-spacing: -0.05em; line-height: 0.85; color: #fff; }
        .v3-price-line em { font-style: normal; font-size: 18px; font-weight: 500; color: rgba(255,255,255,0.72); }
        .v3-price-line s { font-size: 18px; font-weight: 500; text-decoration: line-through; color: rgba(255,255,255,0.72); }
        .v3-off { align-self: center; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; padding: 4px 7px; border-radius: 6px; background: rgba(156,200,255,0.12); color: #9CC8FF; border: 1px solid rgba(156,200,255,0.35); }
        .v3-cancel { display: flex; align-items: center; gap: 8px; max-width: 14ch; font-size: 13px; font-weight: 600; color: #fff; line-height: 1.15; white-space: pre-line; }
        .v3-cancel i { width: 36px; height: 36px; flex: none; border: 1.5px solid rgba(255,255,255,0.9); border-radius: 50%; display: grid; place-items: center; }
        .v3-cancel svg { width: 16px; height: 16px; fill: none; stroke: #fff; stroke-width: 1.7; stroke-linejoin: round; stroke-linecap: round; }
        .v3-perks { list-style: none; margin: 6px 0 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; min-height: 0; }
        .v3-perks li { font-size: 11px; line-height: 1.2; color: #fff; }
        .v3-perks li::before { content: "✓  "; color: #9CC8FF; }
        .v3-prog { display: flex; flex-direction: column; }
        .v3-prog-head { display: flex; flex-direction: row; justify-content: space-between; align-items: center; gap: 8px; }
        .v3-more { color: rgba(255,255,255,0.72); font-size: 12px; font-weight: 500; text-decoration: underline; text-underline-offset: 3px; min-height: 32px; display: inline-flex; align-items: center; }
        .v3-thumbs { list-style: none; margin: 6px 0 0; padding: 0; display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 4px; height: 58px; }
        .v3-thumbs li { position: relative; min-width: 0; height: 100%; border-radius: 10px; overflow: hidden; background: #12141a; }
        .v3-thumbs picture, .v3-thumbs img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .v3-thumbs b { position: absolute; left: 0; right: 0; bottom: 0; margin: 0; padding: 12px 2px 3px; background: linear-gradient(transparent, rgba(0,0,0,0.88)); font-size: 8px; font-weight: 700; letter-spacing: 0.02em; text-align: center; line-height: 1.1; color: #fff; }
        .v3-sticky, .v3-sticky.is-on { display: none; }
        .v3-sticky .v3-ring-wide { max-width: none; }
        .v3 button:focus-visible, .v3 a:focus-visible { outline: 2px solid #9CC8FF; outline-offset: 3px; }
        #mentorat, #formations { scroll-margin-top: 16px; }
        @media (min-width: 1100px) {
          .v3 { padding: 16px 40px 16px; }
          .v3-logo { font-size: 28px; }
          .v3-top { display: grid; grid-template-columns: auto 1fr auto; gap: 16px; flex: none; }
          .v3-nav { display: flex; justify-content: center; align-items: center; gap: 16px; }
          .v3-nav a, .v3-nav span { font-size: 12px; }
          .v3-top-actions { margin-left: 0; gap: 12px; }
          .v3-top .v3-ring > button { padding: 0 16px; font-size: 14px; }
          .v3-copy { display: flex; flex-direction: column; justify-content: center; gap: 8px; grid-area: copy; min-height: 0; }
          .v3-main { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(360px, 500px); grid-template-rows: auto minmax(0, 1fr) minmax(0, 1.05fr); grid-template-areas: "copy call" "points price" "cta prog"; column-gap: 28px; row-gap: 12px; margin-top: 12px; align-items: stretch; }
          .v3-call { grid-area: call; }
          .v3-points { grid-area: points; }
          .v3-price { grid-area: price; min-height: 0; overflow: hidden; }
          .v3-prog { grid-area: prog; min-height: 0; }
          .v3-cta-block { grid-area: cta; align-self: end; }
          .v3-title { font-size: clamp(46px, 6.4svh, 84px); word-spacing: 0.18em; }
          .v3-lead { font-size: clamp(14px, 1.8svh, 18px); max-width: 34em; }
          .v3-points { max-width: 520px; gap: 8px; justify-content: center; }
          .v3-points li { grid-template-columns: 40px minmax(0, 1fr); }
          .v3-ico { width: 40px; height: 40px; }
          .v3-points strong { font-size: 15px; }
          .v3-points span { font-size: 13px; }
          .v3-call { grid-template-columns: minmax(0, 1fr) 140px; }
          .v3-card { padding: 12px 14px; }
          .v3-card h2 { font-size: clamp(20px, 2.6svh, 26px); }
          .v3-card p { font-size: clamp(12px, 1.5svh, 14px); }
          .v3-prog > p { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
          .v3-price-line strong { font-size: clamp(44px, 6svh, 68px); }
          .v3-perks { column-gap: 14px; }
          .v3-perks li { font-size: 13px; }
          .v3-thumbs { height: auto; flex: 1; min-height: 0; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-template-rows: 1fr 1fr; }
          .v3-thumbs b { padding: 22px 4px 6px; font-size: 10px; }
        }
        @media (hover: hover) {
          .v3-nav a:hover::after { transform: scaleX(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .v3-ring::before { animation: none; opacity: 0; }
          .v3-nav a::after { transition: none; }
          .v3-meter i { transition: none; }
        }
      `}</style>
    </div>
  );
}
